const NUMERIC_COLUMN_TYPES = new Set([
  'stackedhorizontalbar',
  'sparkline',
  'posnegbarchart',
  'percent',
  'posnegpercent',
  'posnegpercentbg',
  'numberfloat',
  'numberfloatbg',
]);

const NON_STRING_COLUMN_TYPES = new Set([...NUMERIC_COLUMN_TYPES, 'button', 'link', 'empty']);

export type CellTextAlign = 'left' | 'center' | 'right';

const normalizeColumnType = (dataType?: string) => dataType?.trim().toLowerCase() ?? '';

/** Normalize BE `alignment` (and common aliases) to a cell text align value. */
export const normalizeColumnAlignment = (alignment: unknown): CellTextAlign | undefined => {
  if (alignment === undefined || alignment === null || alignment === '') return undefined;
  const normalized = String(alignment).trim().toLowerCase();
  if (normalized === 'left' || normalized === 'start') return 'left';
  if (normalized === 'right' || normalized === 'end') return 'right';
  if (normalized === 'center' || normalized === 'centre' || normalized === 'middle') return 'center';
  return undefined;
};

export const isTickerColumn = (dataKey: string, dataType?: string, indexTitle?: string): boolean => {
  if (dataKey === 'name' || dataKey === 'Ticker') return true;
  if (normalizeColumnType(dataType) === 'button') return true;
  if (!indexTitle) return false;

  const normalizedKey = dataKey.replace(/_/g, ' ').trim().toLowerCase();
  const normalizedTitle = indexTitle.replace(/_/g, ' ').trim().toLowerCase();
  return normalizedKey === normalizedTitle;
};

/** True for BE `string` / untyped text columns (not numeric chart types). */
export const isPlainTextColumn = (dataType?: string): boolean => {
  const normalized = normalizeColumnType(dataType);
  if (!normalized || normalized === 'string' || normalized === 'text') return true;
  return !NON_STRING_COLUMN_TYPES.has(normalized);
};

export const isNumericColumn = (dataType?: string): boolean => {
  const normalized = normalizeColumnType(dataType);
  return NUMERIC_COLUMN_TYPES.has(normalized);
};

export const getFirstPlainTextColumnId = (
  columns: { dataKey: string; columnId: string; originalIndex: number }[],
  dataTypes: (string | undefined)[] | undefined,
  indexTitle?: string,
): string | null => {
  for (const column of columns) {
    const { dataKey, columnId, originalIndex } = column;
    if (dataKey === 'order' || isTickerColumn(dataKey, dataTypes?.[originalIndex], indexTitle)) {
      continue;
    }
    if (isPlainTextColumn(dataTypes?.[originalIndex])) {
      return columnId;
    }
  }
  return null;
};

/**
 * Resolve cell alignment:
 * 1) BE `alignment` when present
 * 2) string / name / button → left
 * 3) number / percent chart types → center
 */
export const getCellTextAlign = (
  dataKey: string,
  dataType: string | undefined,
  _columnId: string,
  _firstTextColumnId: string | null,
  indexTitle?: string,
  alignment?: unknown,
): CellTextAlign => {
  const fromApi = normalizeColumnAlignment(alignment);
  if (fromApi) return fromApi;

  if (isTickerColumn(dataKey, dataType, indexTitle) || isPlainTextColumn(dataType)) {
    return 'left';
  }

  // Number, percent, and other chart column types default to center.
  return 'center';
};
